import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authService } from '../services/auth';
import { companiesService } from '../services/system/companies';
import { usersService } from '../services/system/users';
import { setAuditActor } from '../services/system/audit';
import { useTranslation } from 'react-i18next';
import { sessionManager } from '../services/auth/session-manager';

const AuthContext = createContext();

// Default companies for initial setup
const defaultCompanies = [
  {
    name: 'ABC Logistics Ltd',
    domain: 'abc-logistics',
    plan: 'professional',
    status: 'active',
    settings: {
      maxUsers: 50,
      maxShipments: 1000,
      features: ['advanced-analytics', 'api-access', 'priority-support'],
      advancedFeatures: {
        bankReconciliation: false,
        paymentTracking: false,
        periodLocking: false
      }
    },
    billing: {
      plan: 'professional',
      amount: 99,
      currency: 'USD',
      interval: 'monthly',
      status: 'active',
      nextBilling: '2024-02-15T10:00:00.000Z'
    },
    contact: {
      adminEmail: 'admin@abc-logistics.com',
      phone: '+1-555-0123',
      address: '123 Logistics Ave, Business District, NY 10001'
    },
    stats: {
      totalUsers: 12,
      activeUsers: 8,
      totalShipments: 145,
      monthlyShipments: 23
    }
  },
  {
    name: 'XYZ Transport Co',
    domain: 'xyz-transport',
    plan: 'enterprise',
    status: 'active',
    settings: {
      maxUsers: 100,
      maxShipments: 5000,
      features: ['advanced-analytics', 'api-access', 'priority-support', 'white-labeling'],
      advancedFeatures: {
        bankReconciliation: false,
        paymentTracking: false,
        periodLocking: false
      }
    },
    billing: {
      plan: 'enterprise',
      amount: 199,
      currency: 'USD',
      interval: 'monthly',
      status: 'active',
      nextBilling: '2024-02-10T08:30:00.000Z'
    },
    contact: {
      adminEmail: 'admin@xyz-transport.com',
      phone: '+1-555-0456',
      address: '456 Transport Blvd, Industrial Zone, CA 90210'
    },
    stats: {
      totalUsers: 25,
      activeUsers: 18,
      totalShipments: 342,
      monthlyShipments: 67
    }
  }
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [isSuperAdminMode, setIsSuperAdminMode] = useState(false);

  // Load user language preference
  const loadUserLanguagePreference = useCallback(async (userId) => {
    try {
      const preferences = await usersService.getUserPreferences(userId);
      if (preferences.language) {
        console.log('AuthContext - Loading user language preference:', preferences.language);
        await i18n.changeLanguage(preferences.language);
      } else {
        console.log('AuthContext - No language preference found, using default (Arabic)');
        await i18n.changeLanguage('ar');
      }
    } catch (error) {
      console.error('Error loading user language preference:', error);
      // Default to Arabic if there's an error
      await i18n.changeLanguage('ar');
    }
  }, [i18n]);

  // Save user language preference
  const saveUserLanguagePreference = useCallback(async (userId, language) => {
    try {
      await usersService.updateUserLanguage(userId, language);
      console.log('AuthContext - Saved user language preference:', language);
    } catch (error) {
      console.error('Error saving user language preference:', error);
    }
  }, []);

  // Load companies from Firebase
  const loadCompanies = useCallback(async () => {
    try {
      setCompaniesLoading(true);
      const companiesData = await companiesService.getCompanies();
      
      // If no companies exist, add default ones (for first-time setup)
      if (companiesData.length === 0 && user?.role === 'superadmin') {
        console.log('No companies found, adding default companies...');
        for (const defaultCompany of defaultCompanies) {
          await companiesService.addCompany(defaultCompany);
        }
        // Reload companies after adding defaults
        const updatedCompanies = await companiesService.getCompanies();
        setCompanies(updatedCompanies);
      } else {
        setCompanies(companiesData);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      // Keep existing companies in case of error
    } finally {
      setCompaniesLoading(false);
    }
  }, [user?.role]);

  // Refresh current company data (useful when company settings are updated)
  const refreshCurrentCompany = useCallback(async () => {
    if (currentCompany?.id) {
      try {
        console.log('AuthContext - Refreshing current company data for:', currentCompany.id);
        const updatedCompany = await companiesService.getCompany(currentCompany.id);
        console.log('AuthContext - Current company refreshed:', updatedCompany);
        setCurrentCompany(updatedCompany);
      } catch (error) {
        console.error('Error refreshing current company:', error);
      }
    }
  }, [currentCompany?.id]);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (userData) => {
      console.log('AuthContext - onAuthStateChanged userData:', userData);
      
      if (userData) {
        // User is signed in
        setUser(userData);
        
        // Start session monitoring
        sessionManager.startMonitoring();
        
        // Update last login and load language preference in parallel
        await Promise.all([
          usersService.updateUserLastLogin(userData.uid).catch(err => console.error('Error updating user last login:', err)),
          loadUserLanguagePreference(userData.uid),
        ]);

        // If user has a companyId, set the current company
        if (userData.companyId) {
          try {
            const company = await companiesService.getCompany(userData.companyId);
            console.log('AuthContext - Loaded company:', company);
            setCurrentCompany(company);
            setIsSuperAdminMode(false);
          } catch (error) {
            console.error('Error loading user company:', error);
            setCurrentCompany(null);
          }
        } else if (userData.role === 'superadmin') {
          console.log('AuthContext - User is superadmin');
          // Super admin doesn't have a specific company by default
          setCurrentCompany(null);
          setIsSuperAdminMode(true);
          // Load all companies for super admin
          await loadCompanies();
        } else {
          console.log('AuthContext - User has no companyId and is not superadmin. Role:', userData.role);
          // For regular company users without companyId, we need to find their company
          // This might happen if the user profile doesn't have companyId set
          if (userData.role === 'admin' || userData.role === 'operations' || userData.role === 'accounting') {
                       console.log('AuthContext - Looking for company by user email or domain');
           try {
             const allCompanies = await companiesService.getCompanies();
             console.log('AuthContext - All companies:', allCompanies);
             
             // Try to match by admin email first (for newly created companies)
             const matchingCompany = allCompanies.find(company => 
               company.contact?.adminEmail === userData.email
             );
             
             if (matchingCompany) {
               console.log('AuthContext - Found matching company by admin email:', matchingCompany);
               setCurrentCompany(matchingCompany);
               setIsSuperAdminMode(false);
             } else {
               console.log('AuthContext - No matching company found by admin email');
               // Fallback: try to match by email domain
               const userEmailDomain = userData.email.split('@')[1];
               const domainMatchingCompany = allCompanies.find(company => 
                 company.contact?.adminEmail && company.contact.adminEmail.split('@')[1] === userEmailDomain
               );
               
               if (domainMatchingCompany) {
                 console.log('AuthContext - Found matching company by domain:', domainMatchingCompany);
                 setCurrentCompany(domainMatchingCompany);
                 setIsSuperAdminMode(false);
               } else {
                 console.log('AuthContext - No matching company found');
                 setCurrentCompany(null);
               }
             }
            } catch (error) {
              console.error('Error finding user company:', error);
              setCurrentCompany(null);
            }
          } else {
            setCurrentCompany(null);
          }
        }
      } else {
        // User is signed out
        console.log('AuthContext - User signed out');
        setUser(null);
        setCurrentCompany(null);
        setCompanies([]);
        setIsSuperAdminMode(false);
        
        // Stop session monitoring
        sessionManager.destroy();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [loadCompanies, loadUserLanguagePreference]);

  const login = async (credentials) => {
    try {
      const userData = await authService.signIn(credentials.email, credentials.password);
      console.log('AuthContext - login userData:', userData);
      setUser(userData);
      
      // Update user's last login time
      try {
        await usersService.updateUserLastLogin(userData.uid);
      } catch (error) {
        console.error('Error updating user last login:', error);
      }
      
      // Load user's language preference
      await loadUserLanguagePreference(userData.uid);
      
      // Set current company if user has one
      if (userData.companyId) {
        console.log('AuthContext - login: User has companyId:', userData.companyId);
        try {
          const company = await companiesService.getCompany(userData.companyId);
          console.log('AuthContext - login: Loaded company:', company);
          setCurrentCompany(company);
          setIsSuperAdminMode(false);
        } catch (error) {
          console.error('Error loading user company:', error);
        }
      } else if (userData.role === 'superadmin') {
        console.log('AuthContext - login: User is superadmin');
        setIsSuperAdminMode(true);
        await loadCompanies();
      } else {
        console.log('AuthContext - login: User has no companyId, trying to find company');
        // Try to find company for regular users
        if (userData.role === 'admin' || userData.role === 'operations' || userData.role === 'accounting') {
          try {
            const allCompanies = await companiesService.getCompanies();
            console.log('AuthContext - login: All companies:', allCompanies);
            
            // Try to match by admin email first (for newly created companies)
            const matchingCompany = allCompanies.find(company => 
              company.contact?.adminEmail === userData.email
            );
            
            if (matchingCompany) {
              console.log('AuthContext - login: Found matching company by admin email:', matchingCompany);
              setCurrentCompany(matchingCompany);
              setIsSuperAdminMode(false);
            } else {
              console.log('AuthContext - login: No matching company found by admin email');
              // Fallback: try to match by email domain
              const userEmailDomain = userData.email.split('@')[1];
              const domainMatchingCompany = allCompanies.find(company => 
                company.contact?.adminEmail && company.contact.adminEmail.split('@')[1] === userEmailDomain
              );
              
              if (domainMatchingCompany) {
                console.log('AuthContext - login: Found matching company by domain:', domainMatchingCompany);
                setCurrentCompany(domainMatchingCompany);
                setIsSuperAdminMode(false);
              }
            }
          } catch (error) {
            console.error('Error finding user company during login:', error);
          }
        }
      }
      
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Keep the audit actor registry in sync with the signed-in user + current company.
  // Services (which can't use React hooks) read from this when writing audit entries.
  useEffect(() => {
    if (user) {
      // User docs in this app store firstName/lastName (and sometimes name).
      // Build a friendly display name and fall back through every plausible field
      // so the audit "Who" column is never empty when we know who the user is.
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      const displayName =
        user.name ||
        user.displayName ||
        (fullName.length > 0 ? fullName : null) ||
        user.email ||
        user.uid;
      setAuditActor({
        uid: user.uid,
        email: user.email,
        name: displayName,
        role: user.role,
        companyId: currentCompany?.id || user.companyId || null
      });
    } else {
      setAuditActor(null);
    }
  }, [user, currentCompany]);

  const logout = async () => {
    try {
      // Stop session monitoring
      sessionManager.destroy();
      
      await authService.signOut();
      setUser(null);
      setCurrentCompany(null);
      setCompanies([]);
      setIsSuperAdminMode(false);
      // Reset language to default (Arabic) when logging out
      await i18n.changeLanguage('ar');
      localStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const switchCompany = async (companyId) => {
    if (user?.role === 'superadmin') {
      try {
        if (companyId) {
          const company = await companiesService.getCompany(companyId);
          setCurrentCompany(company);
        } else {
          setCurrentCompany(null);
        }
        // Don't change isSuperAdminMode - keep super admin privileges
      } catch (error) {
        console.error('Error switching company:', error);
      }
    }
  };

  const returnToSuperAdmin = () => {
    if (user?.role === 'superadmin') {
      setCurrentCompany(null);
      setIsSuperAdminMode(true);
    }
  };

  const isSuperAdmin = () => {
    return user?.role === 'superadmin';
  };

  const getCompanies = () => {
    return companies;
  };

  const getCompany = async (companyId) => {
    try {
      return await companiesService.getCompany(companyId);
    } catch (error) {
      console.error('Error getting company:', error);
      throw error;
    }
  };

  // Company management for Super Admin
  const addCompany = async (companyData) => {
    if (!isSuperAdmin()) {
      throw new Error('Only super admins can add companies');
    }
    
    try {
      console.log('AuthContext - addCompany: Creating company with data:', companyData);
      
      // Ensure company data has the required structure with default advanced features
      const enhancedCompanyData = {
        ...companyData,
        settings: {
          ...companyData.settings,
          advancedFeatures: {
            bankReconciliation: false,
            paymentTracking: false,
            periodLocking: false,
            ...companyData.settings?.advancedFeatures // Allow overrides if provided
          }
        }
      };
      
      // First create the company
      const newCompanyId = await companiesService.addCompany(enhancedCompanyData);
      console.log('AuthContext - addCompany: Company created with ID:', newCompanyId);
      
      // Then create the admin user account
      const adminUserData = {
        role: 'admin',
        companyId: newCompanyId,
        name: `${companyData.name} Admin`,
        phone: companyData.phone || '',
        status: 'active'
      };
      
      console.log('AuthContext - addCompany: Creating admin user:', adminUserData);
      
      await authService.createUser(
        companyData.adminEmail, 
        companyData.adminPassword, 
        adminUserData
      );
      
      console.log('AuthContext - addCompany: Admin user created successfully');
      
      // Reload companies to get updated list
      await loadCompanies();
      return newCompanyId;
    } catch (error) {
      console.error('Error adding company:', error);
      throw error;
    }
  };

  const updateCompany = async (companyId, companyData) => {
    if (!isSuperAdmin()) {
      throw new Error('Only super admins can update companies');
    }
    
    try {
      const result = await companiesService.updateCompany(companyId, companyData);
      // Reload companies to get updated list
      await loadCompanies();
      return result;
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  };

  const deleteCompany = async (companyId) => {
    if (!isSuperAdmin()) {
      throw new Error('Only super admins can delete companies');
    }
    
    try {
      const result = await companiesService.deleteCompany(companyId);
      // Reload companies to get updated list
      await loadCompanies();
      return result;
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  };

    // Permission checking function for modules
  const canAccessModule = (module) => {
    if (!user) return false;

    // Super admin and company admin always have access
    if (user.role === 'superadmin' || user.role === 'admin') {
      return true;
    }

    // Honor explicit per-user permissions when present
    if (user.permissions && typeof user.permissions === 'object') {
      return user.permissions[module] === true;
    }

    // Fallback role-based defaults (for legacy users without permissions object).
    // Keep these aligned with ROLE_PERMISSIONS in components/users/UserModal.jsx.
    if (user.role === 'operations') {
      return ['dashboard', 'shipments', 'clients', 'drivers', 'vehicles', 'quotation'].includes(module);
    }
    if (user.role === 'accounting') {
      return ['dashboard', 'shipments', 'clients', 'accounting', 'quotation', 'vendors'].includes(module);
    }
    if (user.role === 'client') {
      return ['dashboard'].includes(module);
    }
    return false;
  };

  // Check if a specific advanced feature is enabled for the current company
  const hasAdvancedFeature = (featureName) => {
    if (!user) return false;
    
    // Super admin has access to all features
    if (user.role === 'superadmin') {
      return true;
    }
    
    // Check if the company has the feature enabled
    if (!currentCompany?.settings?.advancedFeatures) {
      return false;
    }
    
    return currentCompany.settings.advancedFeatures[featureName] || false;
  };

  // Enable/disable advanced features for a company (superadmin only)
  const updateCompanyFeatures = async (companyId, featureUpdates) => {
    if (!isSuperAdmin()) {
      throw new Error('Only super admins can update company features');
    }
    
    try {
      const company = await companiesService.getCompany(companyId);
      const updatedSettings = {
        ...company.settings,
        advancedFeatures: {
          ...company.settings.advancedFeatures,
          ...featureUpdates
        }
      };
      
      await companiesService.updateCompany(companyId, { settings: updatedSettings });
      
      // Reload companies to get updated list
      await loadCompanies();
      
      // If updating current company, refresh it
      if (currentCompany?.id === companyId) {
        await refreshCurrentCompany();
      }
      
      return true;
    } catch (error) {
      console.error('Error updating company features:', error);
      throw error;
    }
  };

  const value = {
    user,
    currentUser: user, // Add currentUser alias for compatibility
    currentCompany,
    companies,
    loading,
    companiesLoading,
    isSuperAdminMode,
    login,
    logout,
    switchCompany,
    returnToSuperAdmin,
    isSuperAdmin,
    getCompanies,
    getCompany,
    addCompany,
    updateCompany,
    deleteCompany,
    canAccessModule,
    hasAdvancedFeature,
    updateCompanyFeatures,
    loadCompanies,
    loadUserLanguagePreference,
    saveUserLanguagePreference,
    refreshCurrentCompany
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 